import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Search, MessageCircle, HelpCircle, BookOpen, Send, Loader2,
  Ticket, ChevronRight, ThumbsUp, Rocket, Sparkles, CreditCard, Wrench,
  Building, BookOpenCheck, Accessibility, X, Bot, User, Bookmark, BookmarkCheck, Eye
} from 'lucide-react';
import ArticleContent from '@/components/ArticleContent';

// Decode HTML entities like &mdash; &rsquo; &amp; so plain-text renders (title,
// tag chips, list-card excerpts) don't show raw entities. Safe for text nodes.
const decodeEntities = (str) => {
  if (!str || typeof str !== 'string') return str || '';
  if (typeof document === 'undefined') return str;
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
};

const HelpCenter = () => {
  const { api, user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('knowledge');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [aiSearchResult, setAiSearchResult] = useState(null);
  const [flaggedIds, setFlaggedIds] = useState([]);
  const [savedArticles, setSavedArticles] = useState([]);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  
  // Ticket state
  const [tickets, setTickets] = useState([]);
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchCategories();
    fetchArticles();
    if (isAuthenticated) {
      fetchTickets();
      fetchFlaggedIds();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/help/categories');
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchArticles = async (category = null) => {
    try {
      const params = category ? `?category=${category}` : '';
      const res = await api.get(`/help/articles${params}`);
      setArticles(res.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await api.get('/help/tickets');
      setTickets(res.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setAiSearchResult(null);
    
    try {
      const res = await api.post(`/help/ai-search?query=${encodeURIComponent(searchQuery)}`);
      setArticles(res.data.results);
      setAiSearchResult(res.data.ai_summary);
    } catch (error) {
      // Fallback to basic search
      const filtered = articles.filter(a => 
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setArticles(filtered);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject || !ticketForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingTicket(true);
    try {
      await api.post(`/help/tickets?subject=${encodeURIComponent(ticketForm.subject)}&description=${encodeURIComponent(ticketForm.description)}&category=${ticketForm.category}&priority=${ticketForm.priority}`);
      toast.success('Ticket submitted successfully!');
      setTicketForm({ subject: '', description: '', category: 'general', priority: 'medium' });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to submit ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const handleTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    try {
      await api.post(`/help/tickets/${selectedTicket.id}/reply?message=${encodeURIComponent(ticketReply)}`);
      toast.success('Reply sent');
      setTicketReply('');
      fetchTickets();
      // Refresh selected ticket
      const res = await api.get(`/help/tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const params = new URLSearchParams({ message: userMessage });
      if (chatSessionId) params.append('session_id', chatSessionId);
      
      const res = await api.post(`/help/chat?${params.toString()}`);
      setChatSessionId(res.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't process your request. Please try again or submit a support ticket." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const markArticleHelpful = async (articleId) => {
    try {
      const res = await api.post(`/help/articles/${articleId}/helpful`);
      const newCount = res?.data?.helpful_count;
      if (typeof newCount === 'number') {
        setArticles(prev => prev.map(a => a.id === articleId ? { ...a, helpful_count: newCount } : a));
        setSelectedArticle(prev => (prev && prev.id === articleId) ? { ...prev, helpful_count: newCount } : prev);
      }
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Failed to mark helpful:', error);
    }
  };

  const fetchFlaggedIds = async () => {
    try {
      const res = await api.get('/help/flagged/ids');
      setFlaggedIds(res.data.flagged_ids || []);
    } catch (err) { /* not signed in, ignore */ }
  };

  const fetchSavedArticles = async () => {
    try {
      const res = await api.get('/help/flagged');
      setSavedArticles(res.data.articles || []);
      setFlaggedIds(res.data.flagged_ids || []);
    } catch (err) { /* not signed in, ignore */ }
  };

  const toggleFlag = async (articleId, evt) => {
    if (evt) evt.stopPropagation();
    if (!isAuthenticated) {
      toast.info('Sign in to save articles for later');
      return;
    }
    try {
      const res = await api.post(`/help/articles/${articleId}/flag`);
      const isFlagged = res.data.flagged;
      setFlaggedIds(prev => isFlagged ? [...prev, articleId] : prev.filter(id => id !== articleId));
      if (showSavedOnly) fetchSavedArticles();
      toast.success(isFlagged ? 'Saved for later' : 'Removed from saved');
    } catch (err) {
      toast.error('Could not update saved status');
    }
  };

  const getCategoryIcon = (iconName) => {
    const icons = {
      'rocket': Rocket,
      'sparkles': Sparkles,
      'credit-card': CreditCard,
      'accessibility': Accessibility,
      'wrench': Wrench,
      'building': Building,
      'book-open': BookOpenCheck
    };
    return icons[iconName] || HelpCircle;
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-slate-100 text-slate-800'
    };
    return colors[status] || colors['open'];
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-sky-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <HelpCircle className="w-6 h-6 text-sky-500" />
                <h1 className="text-xl font-bold text-sky-800">Help Center</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <Card className="mb-8 bg-gradient-to-r from-sky-500 to-blue-600 text-white">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-2">How can we help you?</h2>
            <p className="text-sky-100 mb-4">Search our knowledge base or chat with our AI assistant</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search for help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 bg-white text-slate-800"
                  data-testid="help-search-input"
                />
              </div>
              <Button 
                onClick={handleSearch} 
                disabled={searching}
                variant="secondary"
                data-testid="help-search-btn"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Search Result */}
        {aiSearchResult && (
          <Card className="mb-6 border-sky-200 bg-sky-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Bot className="w-6 h-6 text-sky-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-sky-800 mb-2">AI Answer</h3>
                  <p className="text-slate-700 whitespace-pre-wrap">{aiSearchResult}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="knowledge" onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="knowledge" className="gap-2" data-testid="tab-knowledge">
              <BookOpen className="w-4 h-4" /> Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="tickets" className="gap-2" data-testid="tab-tickets">
              <Ticket className="w-4 h-4" /> Support Tickets
            </TabsTrigger>
            <TabsTrigger value="chat" className="gap-2" data-testid="tab-chat">
              <MessageCircle className="w-4 h-4" /> AI Chat
            </TabsTrigger>
          </TabsList>

          {/* Knowledge Base Tab */}
          <TabsContent value="knowledge">
            {selectedArticle ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Button 
                  variant="ghost" 
                  onClick={() => setSelectedArticle(null)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to articles
                </Button>
                <Card>
                  {selectedArticle.image_url && (
                    <div className="w-full aspect-[21/9] overflow-hidden rounded-t-lg bg-slate-100">
                      <img
                        src={selectedArticle.image_url}
                        alt={decodeEntities(selectedArticle.title)}
                        className="w-full h-full object-cover"
                        data-testid="article-hero-image"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{decodeEntities(selectedArticle.title)}</CardTitle>
                    <div className="flex gap-2 mt-2">
                      {selectedArticle.tags?.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-sky-100 text-sky-700 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ArticleContent content={selectedArticle.content || ''} />
                    <div className="mt-8 pt-4 border-t flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span data-testid="article-helpful-count" className="flex items-center gap-1">
                          <ThumbsUp className="w-4 h-4 text-emerald-500" />
                          <strong className="text-slate-700">{selectedArticle.helpful_count || 0}</strong> found this helpful
                        </span>
                        {typeof selectedArticle.views === 'number' && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {selectedArticle.views} views
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isAuthenticated && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleFlag(selectedArticle.id)}
                            className="gap-2"
                            data-testid="article-flag-btn"
                          >
                            {flaggedIds.includes(selectedArticle.id) ? (
                              <><BookmarkCheck className="w-4 h-4 fill-amber-500 text-amber-500" /> Saved</>
                            ) : (
                              <><Bookmark className="w-4 h-4" /> Save for later</>
                            )}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => markArticleHelpful(selectedArticle.id)}
                          className="gap-2"
                          data-testid="article-helpful-btn"
                        >
                          <ThumbsUp className="w-4 h-4" /> Yes, it helped
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <div>
                {/* Categories */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {categories.map(cat => {
                    const Icon = getCategoryIcon(cat.icon);
                    return (
                      <Card 
                        key={cat.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => fetchArticles(cat.id)}
                        data-testid={`category-${cat.id}`}
                      >
                        <CardContent className="p-4 text-center">
                          <Icon className="w-8 h-8 mx-auto mb-2 text-sky-500" />
                          <p className="font-medium text-sm">{cat.name}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Articles List */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {showSavedOnly ? 'Saved for later' : 'Popular Articles'}
                  </h3>
                  {isAuthenticated && (
                    <Button
                      variant={showSavedOnly ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        const next = !showSavedOnly;
                        setShowSavedOnly(next);
                        if (next) fetchSavedArticles();
                      }}
                      className="gap-2"
                      data-testid="help-saved-toggle"
                    >
                      {showSavedOnly
                        ? (<><BookmarkCheck className="w-4 h-4 fill-current" /> Showing saved</>)
                        : (<><Bookmark className="w-4 h-4" /> Show saved ({flaggedIds.length})</>)}
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  {(showSavedOnly ? savedArticles : articles).map(article => (
                    <Card
                      key={article.id}
                      className="cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
                      onClick={() => setSelectedArticle(article)}
                      data-testid={`article-${article.id}`}
                    >
                      <CardContent className="p-0 flex items-stretch gap-0">
                        {article.image_url && (
                          <div className="w-32 flex-shrink-0 bg-slate-100 hidden sm:block">
                            <img
                              src={article.image_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1 p-4 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <h4 className="font-medium truncate">{decodeEntities(article.title)}</h4>
                            <p className="text-sm text-slate-500 line-clamp-2">
                              {decodeEntities(
                                (article.content || '')
                                  .replace(/<[^>]+>/g, ' ')          // strip HTML tags
                                  .replace(/^#{1,6}\s+/gm, '')       // strip Markdown headers
                                  .replace(/[*_`~]/g, '')            // strip Markdown inline marks
                                  .replace(/^\s*[-*+]\s+/gm, '')     // strip Markdown list bullets
                                  .replace(/^\s*\d+\.\s+/gm, '')     // strip numbered list markers
                                  .replace(/\s+/g, ' ')
                                  .trim()
                              ).substring(0, 140)}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                              {typeof article.helpful_count === 'number' && article.helpful_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <ThumbsUp className="w-3 h-3 text-emerald-500" /> {article.helpful_count}
                                </span>
                              )}
                              {typeof article.views === 'number' && article.views > 0 && (
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" /> {article.views}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {isAuthenticated && (
                              <button
                                onClick={(e) => toggleFlag(article.id, e)}
                                className="p-2 rounded-md hover:bg-amber-50 transition-colors"
                                title={flaggedIds.includes(article.id) ? 'Remove from saved' : 'Save for later'}
                                data-testid={`article-flag-${article.id}`}
                              >
                                {flaggedIds.includes(article.id)
                                  ? <BookmarkCheck className="w-4 h-4 fill-amber-500 text-amber-500" />
                                  : <Bookmark className="w-4 h-4 text-slate-400" />}
                              </button>
                            )}
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {showSavedOnly && savedArticles.length === 0 && (
                    <div className="text-center py-8 text-slate-500 text-sm">
                      No saved articles yet. Tap the bookmark icon on any article to save it here.
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            {!isAuthenticated ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Ticket className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-xl font-bold mb-2">Login Required</h3>
                  <p className="text-slate-500 mb-4">Please login to submit and view support tickets.</p>
                  <Link to="/login">
                    <Button>Login</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : selectedTicket ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Button 
                  variant="ghost" 
                  onClick={() => setSelectedTicket(null)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to tickets
                </Button>
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{selectedTicket.subject}</CardTitle>
                        <p className="text-sm text-slate-500 mt-1">
                          Created: {new Date(selectedTicket.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-slate-50 p-4 rounded-lg mb-4">
                      <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
                    </div>
                    
                    {/* Responses */}
                    {selectedTicket.responses?.length > 0 && (
                      <div className="space-y-4 mb-4">
                        <h4 className="font-semibold">Responses</h4>
                        {selectedTicket.responses.map((response, i) => (
                          <div 
                            key={response.id || i}
                            className={`p-4 rounded-lg ${response.is_staff ? 'bg-sky-50 border-l-4 border-sky-500' : 'bg-slate-50'}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {response.is_staff ? (
                                <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded">Support Team</span>
                              ) : (
                                <span className="text-xs text-slate-500">You</span>
                              )}
                              <span className="text-xs text-slate-400">
                                {new Date(response.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="whitespace-pre-wrap">{response.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Reply Form */}
                    {selectedTicket.status !== 'closed' && (
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Write a reply..."
                          value={ticketReply}
                          onChange={(e) => setTicketReply(e.target.value)}
                          className="flex-1"
                        />
                        <Button onClick={handleTicketReply} disabled={!ticketReply.trim()}>
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Submit Ticket Form */}
                <Card>
                  <CardHeader>
                    <CardTitle>Submit a Ticket</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmitTicket} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Subject *</label>
                        <Input
                          placeholder="Brief description of your issue"
                          value={ticketForm.subject}
                          onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                          data-testid="ticket-subject"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block">Category</label>
                          <Select 
                            value={ticketForm.category} 
                            onValueChange={(v) => setTicketForm({ ...ticketForm, category: v })}
                          >
                            <SelectTrigger data-testid="ticket-category">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General</SelectItem>
                              <SelectItem value="billing">Billing</SelectItem>
                              <SelectItem value="technical">Technical</SelectItem>
                              <SelectItem value="account">Account</SelectItem>
                              <SelectItem value="feature">Feature Request</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Priority</label>
                          <Select 
                            value={ticketForm.priority} 
                            onValueChange={(v) => setTicketForm({ ...ticketForm, priority: v })}
                          >
                            <SelectTrigger data-testid="ticket-priority">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Description *</label>
                        <Textarea
                          placeholder="Please describe your issue in detail..."
                          value={ticketForm.description}
                          onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                          rows={5}
                          data-testid="ticket-description"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full" 
                        disabled={submittingTicket}
                        data-testid="submit-ticket-btn"
                      >
                        {submittingTicket ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Submit Ticket
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* My Tickets */}
                <Card>
                  <CardHeader>
                    <CardTitle>My Tickets</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {tickets.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No tickets yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {tickets.map(ticket => (
                          <div
                            key={ticket.id}
                            className="p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                            onClick={() => setSelectedTicket(ticket)}
                            data-testid={`ticket-${ticket.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{ticket.subject}</h4>
                                <p className="text-xs text-slate-500">
                                  {new Date(ticket.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(ticket.status)}`}>
                                {ticket.status.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* AI Chat Tab */}
          <TabsContent value="chat">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-sky-500" />
                  AI Support Assistant
                </CardTitle>
                <p className="text-sm text-slate-500">Get instant answers to your questions</p>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden flex flex-col p-0">
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {chatMessages.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Bot className="w-16 h-16 mx-auto mb-4 text-sky-300" />
                      <p className="text-lg font-medium mb-2">Hi! I&apos;m your AI assistant</p>
                      <p className="text-sm">Ask me anything about Zazu!</p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        {['How do I add a child?', 'What are the subscription plans?', 'How does the reward system work?'].map(q => (
                          <Button
                            key={q}
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setChatInput(q);
                            }}
                            className="text-xs"
                          >
                            {q}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <AnimatePresence>
                    {chatMessages.map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-5 h-5 text-sky-600" />
                          </div>
                        )}
                        <div className={`max-w-[80%] p-3 rounded-2xl ${
                          msg.role === 'user' 
                            ? 'bg-sky-500 text-white rounded-br-md' 
                            : 'bg-slate-100 text-slate-800 rounded-bl-md'
                        }`}>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-slate-600" />
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {chatLoading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-sky-600" />
                      </div>
                      <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-md">
                        <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Chat Input */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type your message..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleChatSend()}
                      disabled={chatLoading}
                      data-testid="chat-input"
                    />
                    <Button 
                      onClick={handleChatSend} 
                      disabled={!chatInput.trim() || chatLoading}
                      data-testid="chat-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default HelpCenter;
